import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';

const images = [
  '/assets/khisa1.jpg',
  '/assets/khisa5.jpg',
  '/assets/khisa3.jpg',
  '/assets/khisa8.jpg',
  '/assets/khisa4.jpg',
];

function Hero() {
  const [current, setCurrent] = useState(0);
  const [prev, setPrev] = useState(null);
  const [transitioning, setTransitioning] = useState(false);

  const goTo = useCallback((idx) => {
    if (idx === current || transitioning) return;
    setPrev(current);
    setTransitioning(true);
    setCurrent(idx);
    setTimeout(() => { setPrev(null); setTransitioning(false); }, 1000);
  }, [current, transitioning]);

  useEffect(() => {
    const id = setInterval(() => {
      goTo((current + 1) % images.length);
    }, 6000);
    return () => clearInterval(id);
  }, [current, goTo]);

  return (
    <section className="relative w-full h-[90vh] min-h-[540px] overflow-hidden">

      {/* Previous image (fades out) */}
      {prev !== null && (
        <div
          className="absolute inset-0 transition-opacity duration-1000 ease-in-out opacity-0"
          style={{ backgroundImage: `url(${images[prev]})`, backgroundSize: 'cover', backgroundPosition: 'center' }}
        />
      )}

      {/* Current image (fades in) */}
      <div
        className={`absolute inset-0 transition-opacity duration-1000 ease-in-out ${transitioning ? 'opacity-100' : 'opacity-100'}`}
        style={{ backgroundImage: `url(${images[current]})`, backgroundSize: 'cover', backgroundPosition: 'center' }}
      />

      {/* Multi-layer overlay for depth */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-black/20" />
      <div className="absolute inset-0 bg-gradient-to-r from-black/30 to-transparent" />

      {/* Content */}
      <div className="relative z-10 flex flex-col items-center justify-center h-full text-white text-center px-6">
        <div className="max-w-4xl">
          <p className="animate-fade-in text-red-300 text-sm font-semibold uppercase tracking-[0.2em] mb-4"
            style={{ animationDelay: '0ms' }}>
            Empowering Lives · Shaping Futures
          </p>
          <h1 className="animate-fade-in-up text-4xl md:text-6xl lg:text-7xl font-black leading-[1.08] tracking-tight mb-6"
            style={{ animationDelay: '150ms' }}>
            Khisa Foundation<br />
            <span className="text-red-400">for Impact</span> and<br />Empowerment
          </h1>
          <p className="animate-fade-in-up text-lg md:text-xl text-gray-200 max-w-2xl mx-auto mb-10 leading-relaxed"
            style={{ animationDelay: '300ms' }}>
            We provide essential resources, mentorship, and digital skills to underserved communities — fostering compassion, care, and opportunity for all.
          </p>
          <div className="animate-fade-in-up flex flex-col sm:flex-row gap-4 justify-center"
            style={{ animationDelay: '450ms' }}>
            <Link to="/donate"
              className="bg-red-600 hover:bg-red-700 text-white font-bold px-10 py-4 rounded-full transition-all duration-200 shadow-lg shadow-red-600/30 hover:shadow-red-600/50 hover:-translate-y-0.5">
              Donate Now
            </Link>
            <Link to="/about"
              className="border-2 border-white/60 hover:border-white hover:bg-white/10 text-white font-semibold px-10 py-4 rounded-full transition-all duration-200 backdrop-blur-sm">
              Learn More
            </Link>
          </div>
        </div>
      </div>

      {/* Slide indicators */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 flex gap-2">
        {images.map((_, i) => (
          <button key={i} onClick={() => goTo(i)}
            aria-label={`Go to slide ${i + 1}`}
            className={`transition-all duration-300 rounded-full ${
              i === current ? 'w-8 h-2 bg-white' : 'w-2 h-2 bg-white/40 hover:bg-white/70'
            }`}
          />
        ))}
      </div>

      {/* Scroll hint */}
      <div className="absolute bottom-8 right-8 z-10 hidden md:flex flex-col items-center gap-1 text-white/50">
        <span className="text-xs tracking-widest uppercase">Scroll</span>
        <div className="w-px h-8 bg-white/30 animate-pulse" />
      </div>
    </section>
  );
}

export default Hero;
